// URL Slug oluşturma ve dönüştürme fonksiyonları

/**
 * Türkçe karakterleri İngilizce karşılıklarına çevirir
 */
const turkishToEnglish: Record<string, string> = {
  'ç': 'c', 'Ç': 'C',
  'ğ': 'g', 'Ğ': 'G',
  'ı': 'i', 'İ': 'I',
  'ö': 'o', 'Ö': 'O',
  'ş': 's', 'Ş': 'S',
  'ü': 'u', 'Ü': 'U'
};

/**
 * İngilizce kategori isimlerini Türkçe'ye çevirir
 */
export const categoryTranslations: Record<string, string> = {
  // Genel kategoriler
  'beauty': 'Güzellik',
  'fragrances': 'Parfüm',
  'furniture': 'Mobilya',
  'groceries': 'Market',
  'home-decoration': 'Ev Dekorasyon',
  'kitchen-accessories': 'Mutfak Aksesuarları',
  'laptops': 'Dizüstü Bilgisayar',
  'mens-shirts': 'Erkek Gömlek',
  'mens-shoes': 'Erkek Ayakkabı',
  'mens-watches': 'Erkek Saat',
  'mobile-accessories': 'Telefon Aksesuarları',
  'motorcycle': 'Motosiklet',
  'skin-care': 'Cilt Bakımı',
  'smartphones': 'Akıllı Telefon',
  'sports-accessories': 'Spor Aksesuarları',
  'sunglasses': 'Güneş Gözlüğü',
  'tablets': 'Tablet',
  'tops': 'Üst Giyim',
  'vehicle': 'Araç',
  'womens-bags': 'Kadın Çanta',
  'womens-dresses': 'Kadın Elbise',
  'womens-jewellery': 'Kadın Takı',
  'womens-shoes': 'Kadın Ayakkabı',
  'womens-watches': 'Kadın Saat',
  // Ek kategoriler
  'clothing': 'Giyim',
  'shoes': 'Ayakkabı',
  'accessories': 'Aksesuar',
  'electronics': 'Elektronik',
  'sports': 'Spor',
  'bags': 'Çanta',
  'watches': 'Saat',
  'jewelry': 'Takı',
  'jewellery': 'Takı',
  'home': 'Ev',
  'garden': 'Bahçe',
  'toys': 'Oyuncak',
  'books': 'Kitap',
  'health': 'Sağlık',
  'food': 'Yiyecek',
  'drinks': 'İçecek',
  'baby': 'Bebek',
  'kids': 'Çocuk',
  'men': 'Erkek',
  'women': 'Kadın',
  'unisex': 'Unisex'
};

/**
 * Türkçe kategori isimlerini İngilizce'ye çevirir (veritabanı sorgusu için)
 */
export const categoryTranslationsReverse: Record<string, string[]> = {
  'Giyim': ['clothing', 'mens-shirts', 'womens-dresses', 'tops'],
  'Ayakkabı': ['shoes', 'mens-shoes', 'womens-shoes'],
  'Aksesuar': ['accessories', 'mobile-accessories', 'kitchen-accessories', 'sports-accessories'],
  'Spor': ['sports', 'sports-accessories'],
  'Çanta': ['bags', 'womens-bags'],
  'Elektronik': ['electronics', 'smartphones', 'laptops', 'tablets', 'mobile-accessories'],
  'Güzellik': ['beauty', 'skin-care', 'fragrances'],
  'Mobilya': ['furniture', 'home-decoration'],
  'Saat': ['watches', 'mens-watches', 'womens-watches'],
  'Takı': ['jewelry', 'jewellery', 'womens-jewellery']
};

/**
 * İngilizce kategori ismini Türkçe'ye çevirir
 */
export function translateCategory(category: string): string {
  if (!category) return '';
  const lowerCategory = category.toLowerCase();
  return categoryTranslations[lowerCategory] || category;
}

/**
 * Türkçe kategori isminden veritabanı sorgusu için İngilizce kategorileri döndürür
 */
export function getCategoryFilters(turkishCategory: string): string[] {
  return categoryTranslationsReverse[turkishCategory] || [turkishCategory.toLowerCase()];
}

/**
 * Metni URL-friendly slug'a çevirir
 * Örnek: "Adidas Superstar Ayakkabı" -> "adidas-superstar-ayakkabi"
 */
export function createSlug(text: string): string {
  return text
    .split('')
    .map(char => turkishToEnglish[char] || char)
    .join('')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Özel karakterleri kaldır
    .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
    .replace(/-+/g, '-') // Birden fazla tireyi tek tireye indir
    .replace(/^-+|-+$/g, ''); // Baş ve sondaki tireleri kaldır
}

/**
 * Ürün için SEO uyumlu URL oluşturur
 * Örnek: { id: 123, name: "Adidas Superstar", brand: "Adidas" } 
 * -> "/urun/adidas-superstar-123"
 */
export function createProductUrl(product: { id: number; name: string; brand?: string }): string {
  const nameSlug = createSlug(product.name);
  return `/urun/${nameSlug}-${product.id}`;
}

/**
 * Kategori için SEO uyumlu URL oluşturur
 * Örnek: "Outlet Giyim" -> "/kategori/outlet-giyim"
 */
export function createCategoryUrl(category: string): string {
  const categorySlug = createSlug(category);
  return `/kategori/${categorySlug}`;
}

/**
 * URL slug'ından ID çıkarır
 * Örnek: "adidas-superstar-123" -> 123
 */
export function extractIdFromSlug(slug: string): string | null {
  // Supabase IDs are commonly UUIDs; older imported products may use numbers.
  const match = slug.match(/-([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}|\d+)$/i);
  return match ? match[1] : null;
}

/**
 * Kategori slug'ını okunabilir metne çevirir
 * Örnek: "outlet-giyim" -> "Outlet Giyim"
 */
export function slugToText(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
